import { FC } from "react";

import { useForm } from "react-hook-form";

import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveEditorStepFundraisingGoalStateless } from "./LiveEditorStepFundraisingGoal.stateless";
import { StepFundraisingGoalFormData } from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";

export const TicketingLiveEditorStepFundraisingGoal: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketingForm } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const hadInitialTarget = apiTicketingForm.thermometerTarget !== null;
  const defaultValues: StepFundraisingGoalFormData = {
    target: apiTicketingForm.thermometerTarget || null,
    showInput: apiTicketingForm.thermometerTarget !== null,
  };

  const form = useForm<StepFundraisingGoalFormData>({ defaultValues });

  const target = form.watch("target");
  const showTargetInput = form.watch("showInput");

  const updateTicketingThermometerTarget = trpc.updateTicketingThermometerTarget.useMutation();
  const saveData = async (formData: StepFundraisingGoalFormData) => {
    return await updateTicketingThermometerTarget.mutateAsync({
      ticketingId: apiTicketingForm.id,
      thermometerTarget: formData.target,
    });
  };

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepFundraisingGoalFormData>({
    type: "ticketing",
    form,
    saveData,
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const targetInPreview = formData.target || 100000;
      liveDataToMutate.ticketing.thermometerTarget = targetInPreview;
      liveDataToMutate.placeholders.isFundraisingGoal = !formData.target;
    },
  });

  return (
    <LiveEditorStepFundraisingGoalStateless
      form={form}
      title={t("dashboard", "liveFormEditor.stepFundraisingGoal.ticketing.custom.title")}
      description={t("dashboard", "liveFormEditor.stepFundraisingGoal.ticketing.custom.description")}
      addButtonLabel={t("dashboard", "ticketing.addFundraisingGoal")}
      handleSaveAndBack={handleSaveAndBack}
      handleSaveAndNext={handleSaveAndNext}
      target={target}
      showTargetInput={showTargetInput}
      onAddFundraisingGoal={() => logUserActionEvent(AmplitudeEvents.FundraisingGoalAdded)}
      onRemoveFundraisingGoal={() => logUserActionEvent(AmplitudeEvents.FundraisingGoalRemoved)}
      hadInitialTarget={hadInitialTarget}
    />
  );
};
